import type { MouseEvent, MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type AddressItemRefs = MutableRefObject<Record<number, HTMLDivElement | null>>;
type PopupRef = MutableRefObject<HTMLDivElement | null>;

interface UseAddressOptionsPopupReturn {
  openPopupId: number | null;
  showOptionsPopup: boolean;
  togglePopup: (e: MouseEvent, itemId: number) => void;
  addressOptionsPopupRef: PopupRef;
  addressItemRefs: AddressItemRefs;
}

const useAddressOptionsPopup = (): UseAddressOptionsPopupReturn => {
  const [openPopupId, setOpenPopupId] = useState<number | null>(null);
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);

  const addressOptionsPopupRef = useRef<HTMLDivElement | null>(null);
  const addressItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    const handleClickOutside = (event: Event): void => {
      if (
        showOptionsPopup &&
        addressOptionsPopupRef.current &&
        !addressOptionsPopupRef.current.contains(event.target as Node)
      ) {
        setOpenPopupId(null);
        setShowOptionsPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsPopup]);

  useEffect(() => {
    const handleScroll = (): void => {
      if (showOptionsPopup && !isProgrammaticScroll) {
        setOpenPopupId(null);
        setShowOptionsPopup(false);
      }
    };

    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [showOptionsPopup, isProgrammaticScroll]);

  const togglePopup = useCallback(
    (e: MouseEvent, itemId: number) => {
      e.stopPropagation();
      e.preventDefault();

      if (openPopupId === itemId) {
        setOpenPopupId(null);
        setShowOptionsPopup(false);
      } else {
        setOpenPopupId(itemId);
        setShowOptionsPopup(true);

        setTimeout(() => {
          if (addressItemRefs.current[itemId]) {
            const itemElement = addressItemRefs.current[itemId] as HTMLElement;
            const itemRect = itemElement.getBoundingClientRect();
            const containerElement = itemElement.closest('.list');

            if (containerElement) {
              const containerRect = containerElement.getBoundingClientRect();

              const popupHeight = 80;
              const availableSpaceBelow =
                containerRect.bottom - itemRect.bottom;
              const availableSpaceAbove = itemRect.top - containerRect.top;

              const isEnoughSpaceForPopup = availableSpaceBelow >= popupHeight;
              const shouldScrollForPopup =
                !isEnoughSpaceForPopup &&
                availableSpaceBelow < availableSpaceAbove;
              const shouldScrollForItem =
                itemRect.bottom > containerRect.bottom ||
                itemRect.top < containerRect.top;

              if (shouldScrollForItem || shouldScrollForPopup) {
                setIsProgrammaticScroll(true);

                const scrollPosition = shouldScrollForPopup
                  ? itemRect.top -
                    containerRect.top -
                    (containerRect.height - itemRect.height - popupHeight) / 2
                  : itemRect.top -
                    containerRect.top -
                    (containerRect.height - itemRect.height) / 2;

                containerElement.scrollTo({
                  top: containerElement.scrollTop + scrollPosition,
                  behavior: 'smooth'
                });

                setTimeout(() => {
                  setIsProgrammaticScroll(false);
                }, 500);
              }
            }
          }
        }, 50);
      }
    },
    [openPopupId]
  );

  return {
    openPopupId,
    showOptionsPopup,
    togglePopup,
    addressOptionsPopupRef,
    addressItemRefs
  };
};

export default useAddressOptionsPopup;
